import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import InlineAlert from '../../../shared/components/InlineAlert';
import { DesktopRecommendedArea } from '../../../shared/components/DesktopRecommendedNotice';
import { Rule as RulesIcon } from '@mui/icons-material';
import EmptyStateMessage from '../../../shared/components/EmptyStateMessage';
import { EntityListPanel, EntityListItemConfig } from '../../../shared/components/EntityListPanel';
import RuleEditor from './RuleEditor';
import type {
  EventRuleListItemExtended,
  EventRuleExtended,
  RuleTemplate,
  LiveEvent,
} from '../../../types/frontend';
import { OpsItemStrategy } from '../../../types/backend';

export type RuleEditorMode =
  | { kind: 'none' }
  | { kind: 'editing'; ruleItem: EventRuleListItemExtended; rule: EventRuleExtended }
  | { kind: 'new' }
  | { kind: 'from-template'; template: RuleTemplate }
  | { kind: 'from-event'; event: LiveEvent };

const RULE_TYPE_LABELS: Record<string, string> = {
  alert: 'Alert',
  automation: 'Automation',
};

const STRATEGY_BADGE: Record<string, string> = {
  [OpsItemStrategy.TRACK]: 'Track',
  [OpsItemStrategy.ALWAYS_NEW]: 'Always new',
  [OpsItemStrategy.FINGERPRINT]: 'Fingerprint',
};

const ruleListConfig: EntityListItemConfig<EventRuleListItemExtended> = {
  getPrimaryText: (rule) => rule.name,
  getSecondaryText: (rule) => {
    const ruleType = RULE_TYPE_LABELS[rule.rule_type] || 'Alert';
    const trigger = rule.trigger_type === 'schedule' ? 'Schedule' : 'Event';
    const status = rule.status === 'ENABLED' ? 'Enabled' : 'Disabled';
    const strategy = STRATEGY_BADGE[rule.opsitem_strategy ?? OpsItemStrategy.TRACK];
    return `${ruleType} | ${trigger} | ${status} | ${strategy}`;
  },
  getId: (rule) => rule.rule_id,
  getSearchText: (rule) => `${rule.name} ${rule.description ?? ''}`,
};

interface RulesTabContentProps {
  customerId: string | undefined;
  rules: EventRuleListItemExtended[];
  ruleMode: RuleEditorMode;
  ruleEditorKey: string;
  loadingRule: boolean;
  error: string | null;
  onSelectRule: (rule: EventRuleListItemExtended) => void;
  onNewRule: () => void;
  onRuleSaved: (savedRule: EventRuleExtended) => void;
  onDeleteRule?: () => void;
  onDiscardRule: () => void;
  onClearError: () => void;
}

const RulesTabContent: React.FC<RulesTabContentProps> = ({
  customerId,
  rules,
  ruleMode,
  ruleEditorKey,
  loadingRule,
  error,
  onSelectRule,
  onNewRule,
  onRuleSaved,
  onDeleteRule,
  onDiscardRule,
  onClearError,
}) => {
  const selectedRuleItem = ruleMode.kind === 'editing' ? ruleMode.ruleItem : null;
  const showRuleEditor = ruleMode.kind !== 'none';

  return (
    <DesktopRecommendedArea>
      <Box sx={{ width: 320, display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
        <EntityListPanel
          entities={rules}
          selectedEntity={selectedRuleItem}
          isNew={showRuleEditor && ruleMode.kind !== 'editing'}
          saving={false}
          config={ruleListConfig}
          addButtonText="New Rule"
          emptyStateText="No rules configured"
          emptyStateHelperText="Create your first rule to route events"
          onSelect={onSelectRule}
          onAddNew={onNewRule}
          isLoading={false}
          searchable
        />
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {error && (
          <InlineAlert severity="error" onClose={onClearError} sx={{ mx: 3, mt: 2 }}>
            {error}
          </InlineAlert>
        )}
        {loadingRule ? (
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : showRuleEditor && customerId ? (
          <RuleEditor
            key={ruleEditorKey}
            customerId={customerId}
            rule={ruleMode.kind === 'editing' ? ruleMode.rule : null}
            template={ruleMode.kind === 'from-template' ? ruleMode.template : null}
            fromEvent={ruleMode.kind === 'from-event' ? ruleMode.event : null}
            onSaved={onRuleSaved}
            onDelete={ruleMode.kind === 'editing' ? onDeleteRule : undefined}
            onDiscard={onDiscardRule}
          />
        ) : (
          <EmptyStateMessage
            icon={RulesIcon}
            title="Select a rule to edit"
            description="Choose a rule from the list or create a new one"
          />
        )}
      </Box>
    </DesktopRecommendedArea>
  );
};

export default RulesTabContent;
